import React from "react";
import {
  DateField,
  ExportButton,
  List,
  NumberField,
  TextField as RaTextField,
  useSidebarState,
  useTranslate,
} from "react-admin";
import jsonexport from "jsonexport/dist";
import { downloadCSV } from "react-admin";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CustomDatagrid,
  FilterField,
  FilterAutocomplete,
  ListActionsBar,
} from "@/components/common";
import {
  getAllCostCenters,
  getAllNotifyApplications,
} from "@/features/accounts/services/accountService";
import type { CreditEventLogRecord } from "../types/creditEventLog";

const i18nBase = "resources.credit-consumption/credit-event-logs";

// Stored Channel values are display names; combo offers the full catalogue.
const CHANNELS = [
  "Email",
  "SMS",
  "WhatsApp",
  "Telegram",
  "Viber",
  "LINE",
  "Web Push",
  "App Push",
  "Slack",
  "Teams",
  "Discord",
  "Google Chat",
  "WeCom",
  "Mattermost",
  "Rocket.Chat",
  "Inbox",
  "Webhook",
];

const exporter = (records: CreditEventLogRecord[]): void => {
  const rows = records.map((r) => ({
    costCenter: r.costCenterName,
    account: r.account ?? "",
    application: r.applicationName,
    channel: r.channel,
    provider: r.providerName ?? "",
    from: r.from,
    to: r.to,
    creditsConsumed: r.creditsConsumed,
    date: r.createdAt,
  }));
  void jsonexport(rows, (err: unknown, csv: string) => {
    if (!err) downloadCSV(csv, "credit-event-logs");
  });
};

interface ListActionsProps {
  costCenterNames: string[];
  applicationNames: string[];
}

const ListActions: React.FC<ListActionsProps> = ({
  costCenterNames,
  applicationNames,
}) => {
  const translate = useTranslate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterAutocomplete
            source="costCenterName"
            label={translate(`${i18nBase}.filters.costCenterName`)}
            options={costCenterNames}
          />
          <FilterAutocomplete
            source="applicationName"
            label={translate(`${i18nBase}.filters.applicationName`)}
            options={applicationNames}
          />
          <FilterAutocomplete
            source="communicationChannel"
            label={translate(`${i18nBase}.filters.channel`)}
            options={CHANNELS}
          />
          <FilterField
            source="from"
            label={translate(`${i18nBase}.filters.from`)}
          />
          <FilterField
            source="to"
            label={translate(`${i18nBase}.filters.to`)}
          />
        </>
      }
      addButton={<ExportButton label={translate("ra.action.export")} />}
    />
  );
};

export const CreditEventLogsList: React.FC = () => {
  const translate = useTranslate();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const [costCenterNames, setCostCenterNames] = React.useState<string[]>([]);
  const [applicationNames, setApplicationNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    void getAllCostCenters()
      .then((list) => setCostCenterNames(list.map((c) => c.name).filter(Boolean)))
      .catch(() => undefined);
    void getAllNotifyApplications()
      .then((list) => setApplicationNames(list.map((a) => a.name).filter(Boolean)))
      .catch(() => undefined);
  }, []);

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: "text.primary", mb: 1, mt: 0, ml: 2 }}
      >
        {translate(`${i18nBase}.name`, { smart_count: 2 })}
      </Typography>
      <List
        actions={
          <ListActions
            costCenterNames={costCenterNames}
            applicationNames={applicationNames}
          />
        }
        sort={{ field: "createdAt", order: "DESC" }}
        storeKey={false}
        disableSyncWithLocation
        perPage={10}
        exporter={exporter}
        sx={{
          maxWidth,
          overflow: "visible",
          "& .RaList-main, & .RaList-content": {
            overflow: "visible",
            width: "100%",
            maxWidth: "100%",
          },
          "& .RaListToolbar-root, & .RaList-actions": {
            display: "flex",
            width: "100%",
            mb: { xs: 1, sm: 2 },
            px: 0,
            py: { xs: 0, sm: 1 },
            minHeight: "auto",
          },
        }}
      >
        <CustomDatagrid
          primarySource="costCenterName"
          bulkActionButtons={false}
          disableMobileView
        >
          <RaTextField source="costCenterName" />
          <RaTextField
            source="account"
            label={translate(`${i18nBase}.fields.account`)}
          />
          <RaTextField source="applicationName" />
          <RaTextField source="channel" />
          <RaTextField
            source="providerName"
            label={translate(`${i18nBase}.fields.provider`)}
          />
          <RaTextField
            source="from"
            label={translate(`${i18nBase}.fields.from`)}
          />
          <RaTextField source="to" />
          <NumberField source="creditsConsumed" />
          <DateField
            source="createdAt"
            showTime
            options={{
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }}
          />
        </CustomDatagrid>
      </List>
    </Box>
  );
};

export default CreditEventLogsList;
